import {
  useRef, useState, useEffect,
} from 'react'

// NOTE: This hook consolidates the logic involved in supporting the desired UX
// when sending messages: The user message floats to the top, and the agent
// message fills the empty region below. This is a complex sequence that
// involves watching for state changes, inserting items into the DOM, measuring
// them when the DOM is rendered, and proceeding to the next stage.
//
// This hook should be portable to be used in the OraclePage code as well, as it
// is much more legible and single-purpose than what is currently there.

/**
 * State machine states for auto-scroll positioning during streaming.
 *
 * Flow: IDLE → USER_MSG → SPACER_SIZING → SCROLLING → STREAMING → COMPLETING → SHRINKING → IDLE
 *
 * Each state represents an intent:
 * - IDLE: Waiting for user input
 * - USER_MSG: Pending message is rendering, wait for DOM
 * - SPACER_SIZING: Measure message height, calculate and set spacer
 * - SCROLLING: Perform scroll to position message at top
 * - STREAMING: Response is streaming, wait for completion
 * - COMPLETING: New message added to array, hide streaming UI to prevent duplicate
 * - SHRINKING: Reduce spacer to visible portion (prevents visual jump when done)
 */
type ScrollState = 'IDLE' | 'USER_MSG' | 'SPACER_SIZING' | 'SCROLLING' | 'STREAMING' | 'COMPLETING' | 'SHRINKING'

export interface UseChatAutoScrollOptions {
  /** Whether the chat is currently streaming a response */
  isStreaming?: boolean
  /** Current length of the messages array (used to detect when new message is added) */
  messagesLength?: number
}

export interface UseChatAutoScrollResult {
  /** Height for the spacer element */
  spacerHeight: number
  /** Ref to attach to the scroll container */
  containerRef: React.RefObject<HTMLDivElement>
  /** Ref to attach to the pending message wrapper */
  pendingMessageRef: React.RefObject<HTMLDivElement>
  /** Whether to show the streaming UI (pending message + streaming text) */
  showStreamingUI: boolean
  /** Ref to attach to the spacer element (for shrinking after streaming) */
  spacerRef: React.RefObject<HTMLDivElement>
}

export const SPACER_MIN = 64

/**
 * Hook to manage auto-scroll positioning during chat streaming.
 *
 * When streaming begins, this hook:
 * 1. Waits for the pending user message to render
 * 2. Measures the message height
 * 3. Calculates a spacer height to position the message at the top
 * 4. Scrolls the container to show the message at the top
 *
 * When streaming ends, the spacer shrinks to its visible portion (minimum 64px)
 * to prevent visual jumping.
 *
 * @example
 * ```tsx
 * const {
 *   spacerHeight,
 *   containerRef,
 *   pendingMessageRef,
 *   showStreamingUI,
 *   spacerRef,
 * } = useChatAutoScroll({
 *   isStreaming,
 *   messagesLength: messages.length,
 * })
 *
 * return (
 *   <div ref={containerRef}>
 *     {messages.map(...)}
 *     {showStreamingUI && (
 *       <div ref={pendingMessageRef}>
 *         <UserMessage ... />
 *         <StreamingResponse ... />
 *       </div>
 *     )}
 *     <div ref={spacerRef} style={{ height: spacerHeight }} />
 *   </div>
 * )
 * ```
 */
export const useChatAutoScroll = ({
  isStreaming,
  messagesLength,
}: UseChatAutoScrollOptions): UseChatAutoScrollResult => {
  const containerRef = useRef<HTMLDivElement>(null)
  const pendingMessageRef = useRef<HTMLDivElement>(null)
  const spacerRef = useRef<HTMLDivElement>(null)
  const [scrollState, setScrollState] = useState<ScrollState>('IDLE')
  const [spacerHeight, setSpacerHeight] = useState(SPACER_MIN)
  const prevMessagesLength = useRef(messagesLength ?? 0)
  // Track if completion detected for current streaming session (reset when streaming starts)
  const hasCompletedRef = useRef(false)

  // Derive showStreamingUI during render (not in effect!) to prevent duplicate content
  // If we're streaming and messages just increased, completion happened - hide
  // the streaming UI immediately
  const currentLength = messagesLength ?? 0
  const messagesJustIncreased = currentLength > prevMessagesLength.current

  if (messagesJustIncreased && scrollState === 'STREAMING') {
    hasCompletedRef.current = true
  }

  // showStreamingUI is true only during active streaming states AND completion hasn't been detected
  const showStreamingUI = !hasCompletedRef.current && (
    scrollState === 'USER_MSG'
    || scrollState === 'SPACER_SIZING'
    || scrollState === 'SCROLLING'
    || scrollState === 'STREAMING'
  )

  // Entry transition: detect streaming start
  useEffect(() => {
    if (isStreaming && scrollState === 'IDLE') {
      hasCompletedRef.current = false // Reset completion flag for new streaming session
      setScrollState('USER_MSG')
    }
  }, [isStreaming, scrollState])

  // Main state machine effect
  useEffect(() => {
    switch (scrollState) {
      case 'USER_MSG':
        // Wait for pending message to render, then transition
        requestAnimationFrame(() => {
          setScrollState('SPACER_SIZING')
        })
        break

      case 'SPACER_SIZING':
        // Measure pending message and calculate spacer
        if (containerRef.current && pendingMessageRef.current) {
          const containerHeight = containerRef.current.clientHeight
          const messageHeight = pendingMessageRef.current.offsetHeight
          const newSpacerHeight = Math.max(containerHeight - messageHeight - 16, 0)
          setSpacerHeight(newSpacerHeight)
          // Transition happens in spacerHeight effect below
        }
        break

      case 'SCROLLING':
        // Scroll to bottom, then transition to streaming
        requestAnimationFrame(() => {
          if (containerRef.current) {
            containerRef.current.scrollTo({
              top: containerRef.current.scrollHeight,
              behavior: 'smooth',
            })
          }
          setScrollState('STREAMING')
        })
        break

      case 'STREAMING':
      case 'IDLE':
        // No action needed
        break

      case 'COMPLETING':
        // Brief state to coordinate the transition, then shrink spacer
        requestAnimationFrame(() => {
          setScrollState('SHRINKING')
        })
        break

      case 'SHRINKING':
        // Shrink spacer to only the visible portion (no visual jump)
        requestAnimationFrame(() => {
          if (containerRef.current && spacerRef.current) {
            const container = containerRef.current
            const spacer = spacerRef.current

            const viewportBottom = container.scrollTop + container.clientHeight
            const spacerTop = spacer.offsetTop

            // Only keep the visible portion of spacer, or 64px, whichever is larger
            const visibleSpacerHeight = Math.max(64, viewportBottom - spacerTop)
            setSpacerHeight(visibleSpacerHeight)
          }
          setScrollState('IDLE')
        })
        break

      default:
        break
    }
  }, [scrollState])

  // Spacer height change triggers scroll transition
  useEffect(() => {
    if (spacerHeight > 0 && scrollState === 'SPACER_SIZING') {
      // Spacer has been set, wait for render then scroll
      requestAnimationFrame(() => {
        setScrollState('SCROLLING')
      })
    }
  }, [spacerHeight, scrollState])

  // Transition state machine when messages increase during streaming
  // Note: showStreamingUI is derived during render and already false by now
  useEffect(() => {
    if (scrollState === 'STREAMING' && hasCompletedRef.current) {
      setScrollState('COMPLETING')
    }
  }, [messagesLength, scrollState])

  // Update prevMessagesLength ref after each render
  useEffect(() => {
    prevMessagesLength.current = messagesLength ?? 0
  }, [messagesLength])

  // Exit transition: streaming stopped
  useEffect(() => {
    if (scrollState === 'STREAMING' && !isStreaming) {
      setScrollState('IDLE')
    }
  }, [isStreaming, scrollState])

  return {
    spacerHeight,
    containerRef,
    pendingMessageRef,
    showStreamingUI,
    spacerRef,
  }
}
